import * as React from 'react'

import { BarDatum } from '@nivo/bar'
import { useTheme } from '@nivo/core'
import { useTooltip } from '@nivo/tooltip'
import { animated, to } from '@react-spring/web'

import { BAR_MAX_WIDTH, DEFAULT_BORDER_RADIUS } from './constants'
import { useBarEventHandlers } from './eventHandlers'
import { useBarAnimations, useStackHover, useStackSelection } from './hooks'
import type { ExtendedBarItemProps } from './types'
import {
  calculateFillOpacity,
  createRoundedRectPath,
  extractStackId,
  isBottomBar,
  isTopBar,
} from './utils'

const BarItem = <RawDatum extends BarDatum>({
  bar: { data, ...bar },
  style: { borderColor, color, height, labelColor, labelOpacity, labelX, labelY },
  borderRadius,
  borderWidth,
  label,
  shouldRenderLabel,
  isInteractive,
  onClick,
  onMouseEnter,
  onMouseLeave,
  groupMode = 'grouped',
  keys = [],
  stackAnalysis = {},
  originalData = [],
  customTooltip,
  colors = [],
  selectableBars = true,
}: ExtendedBarItemProps<RawDatum>) => {
  const theme = useTheme()
  const { hoveredStackIndex, setHoveredStackIndex } = useStackHover()
  const { selectedStackIndexes, toggleSelection } = useStackSelection()
  const { showTooltipFromEvent, hideTooltip } = useTooltip()

  // Extract stack ID for this bar
  const stackId = React.useMemo(() => {
    if (!bar || !data) return 'unknown'
    return extractStackId(bar, data, groupMode)
  }, [bar, data, groupMode])

  // Determine hover and selection states
  const isHovered = selectableBars && hoveredStackIndex === stackId
  const isAnySelected = selectableBars && selectedStackIndexes.length > 0
  const isActive = selectableBars ? !isAnySelected || selectedStackIndexes.includes(stackId) : true

  // Calculate fill opacity
  const fillOpacity = React.useMemo(() => {
    if (!bar || !data) return 1
    if (!selectableBars) return 1
    return calculateFillOpacity(data, originalData, bar, stackId, isAnySelected, isActive)
  }, [data, originalData, bar, stackId, isAnySelected, isActive, selectableBars])

  // Calculate bar dimensions
  const barWidth = bar ? Math.min(bar.width, BAR_MAX_WIDTH) : 0
  const xPosition = bar ? bar.x + bar.width / 2 - barWidth / 2 : 0
  const yPosition = bar ? bar.y : 0

  // Set up animations
  const spring = useBarAnimations(
    barWidth,
    bar?.height || 0,
    xPosition,
    yPosition,
    height,
    isHovered
  )

  // Set up event handlers - create empty bar for type safety
  const safeBar: BarDatum = bar || {
    key: '',
    index: 0,
    value: 0,
    formattedValue: '',
    color: '',
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    data: data || {},
  }

  const { handleClick, handleMouseEnter, handleMouseMove, handleMouseLeave } =
    useBarEventHandlers<RawDatum>({
      bar: safeBar,
      data,
      stackId,
      isInteractive: isInteractive ?? false,
      groupMode,
      originalData,
      keys,
      colors,
      color,
      customTooltip,
      onClick,
      onMouseEnter,
      onMouseLeave,
      toggleSelection,
      setHoveredStackIndex,
      showTooltipFromEvent,
      hideTooltip,
    })

  // Early return if no bar or data
  if (!bar || !data) {
    return null
  }

  // Determine if rounded corners should be applied for stacked charts
  const isStacked = groupMode === 'stacked' && keys.length > 1
  const roundTop = isStacked ? isTopBar(bar, stackAnalysis || {}) : true
  const roundBottom = isStacked ? isBottomBar(bar, stackAnalysis || {}) : true

  return (
    <animated.g transform={spring.transform}>
      <animated.path
        d={to([spring.width, spring.height], (ws, hs) =>
          createRoundedRectPath(
            Math.max(ws, 0),
            Math.max(hs, 0),
            Math.max(borderRadius || 0, DEFAULT_BORDER_RADIUS),
            roundTop,
            roundBottom
          )
        )}
        fill={color}
        opacity={fillOpacity}
        stroke={borderColor}
        strokeWidth={borderWidth}
        style={{
          cursor: isInteractive && selectableBars ? 'pointer' : 'default',
          pointerEvents: isInteractive ? 'all' : 'none',
        }}
        onClick={isInteractive && selectableBars ? handleClick : undefined}
        onMouseEnter={isInteractive ? handleMouseEnter : undefined}
        onMouseMove={isInteractive ? handleMouseMove : undefined}
        onMouseLeave={isInteractive ? handleMouseLeave : undefined}
      />

      {shouldRenderLabel && (
        <animated.text
          x={labelX}
          y={labelY}
          fillOpacity={labelOpacity}
          textAnchor="middle"
          dominantBaseline="central"
          style={{
            ...theme.labels.text,
            pointerEvents: 'none',
            fill: labelColor,
          }}
        >
          {label}
        </animated.text>
      )}
    </animated.g>
  )
}

export default BarItem
export { StackProvider } from './hooks'
